import { useState, useCallback, useRef } from "react";
import { decodeObject } from "@salespark/toolkit";
import { withAuth } from "@salespark/api-client-v2";
import { createAPIClient, getDpopPublicJwk } from "utils/helper";
import {
  toggleCollapsedNav,
  onNavTypeChange,
  onTopNavColorChange,
  onHeaderNavColorChange,
  onSwitchTheme,
  onLocaleChange,
  onDirectionChange,
} from "redux/actions/Theme";
import store from "redux/store";
import { sha256Hex, appEnvironment, isXMAS, getAuth0LogoutOptions } from "./auth0Helpers";
import { exposeDevSessionDump } from "./devSession";

const publicApi = withAuth(createAPIClient());

const MAX_SESSION_BOOTSTRAP_ATTEMPTS = 5;
const RETRY_DELAY_MS = 2500;
const REAUTH_GUARD_KEY = "sp:auth-reauth-pending";

/**
 * Detects the server's canonical "needs reauth" response from /auth.
 * The API returns { status: false, data: "Missing params" } whenever the id_token
 * is absent, expired or otherwise invalid - that's our cue to send the user back
 * through Auth0 instead of retrying or showing the failed screen.
 */
const isMissingParamsResponse = (ret) => {
  if (ret === undefined || ret === null || ret.status !== false) {
    return false;
  }
  const d = ret.data;
  if (typeof d === "string") {
    return d.toLowerCase().includes("missing params");
  }
  if (typeof d === "object" && d !== null) {
    const msg = [d.message, d.error, d.reason].filter(Boolean).join(" ").toLowerCase();
    return msg.includes("missing params");
  }
  return false;
};

/**
 * Detects invalid/expired Auth0 id_token from /auth (TokenVerifyError).
 * The API returns { status: false, error: "RequestFailed", message: "TokenVerifyError" }.
 * Clear Auth0 session immediately - retries cannot fix a bad token.
 */
const isTokenVerifyErrorResponse = (ret) => {
  if (ret === undefined || ret === null || ret.status !== false) {
    return false;
  }
  const d = ret.data;
  if (typeof d === "string") {
    return d.toLowerCase().includes("tokenverifyerror");
  }
  if (typeof d === "object" && d !== null) {
    const msg = [d.message, d.error, d.reason].filter(Boolean).join(" ").toLowerCase();
    return msg.includes("tokenverifyerror");
  }
  return false;
};

/**
 * Producer session bootstrap after Auth0: exchanges id_token for SalesPark session + theme from settings.
 */
export const useProducerServerAuth = ({ getAccessTokenSilently, getIdTokenClaims, user, setAuth, loginWithRedirect, logout }) => {
  const [tryMsg, setTryMsg] = useState("A iniciar sessão");
  const [loginStatus, setLoginStatus] = useState({
    running: false,
    completed: false,
    success: false,
  });

  const attemptRef = useRef(0);

  const scheduleTransientRetry = (attemptOrdinal) => {
    setTimeout(() => {
      setTryMsg(`A tentar autenticar. (tentativa ${attemptOrdinal}/${MAX_SESSION_BOOTSTRAP_ATTEMPTS})`);
      setLoginStatus({
        running: false,
        completed: false,
        success: false,
      });
    }, RETRY_DELAY_MS);
  };

  const serverAuth = useCallback(async () => {
    if (loginStatus.completed) return;

    setLoginStatus({
      running: true,
      completed: false,
      success: false,
    });

    try {
      attemptRef.current += 1;
      const attempt = attemptRef.current;

      let idTokenClaims = await getIdTokenClaims();
      let id_token = idTokenClaims && typeof idTokenClaims === "object" ? idTokenClaims.__raw : null;

      if (!id_token) {
        try {
          const tokenResult = await getAccessTokenSilently({ detailedResponse: true, cacheMode: "off" });
          id_token = tokenResult && typeof tokenResult === "object" ? tokenResult.id_token : null;
        } catch (error) {
          // Silent token failures (e.g. login_required) are handled below by the redirect fallback;
          // do not bubble up - we still want to try the cache once more before forcing a redirect.
        }
      }

      if (!id_token) {
        idTokenClaims = await getIdTokenClaims();
        id_token = idTokenClaims && typeof idTokenClaims === "object" ? idTokenClaims.__raw : null;
      }

      // Enrol the DPoP device public key so the API can pin it to this session (RFC 9449). Skipped
      // when the browser cannot hold a key (Safari private mode, no WebCrypto): no jwk, no DPoP.
      const dpopJwkResponse = await getDpopPublicJwk();
      const jwk = dpopJwkResponse?.status === true ? dpopJwkResponse.data : undefined;

      // Always hit /auth so the server has the final word - even when id_token is
      // null, the server will respond with "Missing params" which we treat as a
      // reauth signal below. The failed screen must never appear before /auth runs.
      const ret = await publicApi.post("/auth", {
        id_token,
        guserid: user?.sub,
        name: user?.name,
        picture: user?.picture,
        email: user?.email,
        jwk,
      });

      if (ret.status === false) {
        if (isTokenVerifyErrorResponse(ret)) {
          sessionStorage.removeItem(REAUTH_GUARD_KEY);
          logout(getAuth0LogoutOptions());
          return;
        }

        if (isMissingParamsResponse(ret)) {
          if (sessionStorage.getItem(REAUTH_GUARD_KEY) === "1") {
            // Loop detected: even after the Auth0 round-trip the server still
            // rejects the id_token - surface the failed screen so the user can
            // logout/retry manually.
            sessionStorage.removeItem(REAUTH_GUARD_KEY);
            setLoginStatus({
              running: false,
              completed: true,
              success: false,
            });
            return;
          }
          sessionStorage.setItem(REAUTH_GUARD_KEY, "1");
          await loginWithRedirect();
          return;
        }

        setLoginStatus({
          running: false,
          completed: true,
          success: false,
        });
        return;
      }

      sessionStorage.removeItem(REAUTH_GUARD_KEY);

      try {
        let c = ret.data;
        if (c !== null && typeof c === "object" && Object.prototype.hasOwnProperty.call(c, "v2")) {
          const guseridHashed = await sha256Hex(user.sub.split("|")[1]);
          if (!guseridHashed) {
            setLoginStatus({
              running: false,
              completed: true,
              success: false,
            });
            return;
          }
          const decodeResponse = decodeObject(c.v2, guseridHashed);
          if (!decodeResponse.status) {
            setLoginStatus({
              running: false,
              completed: true,
              success: false,
            });
            return;
          }
          c = decodeResponse.data;
        }

        c.user = user;

        const adminToken = localStorage.getItem(`${appEnvironment}-salespark-admin-token`);

        if (!!c.picture) c.user.picture = c.picture;
        if (!!c.name) c.user.name = c.name;
        c.user.onboarding_completed = c.onboarding_completed;
        c.user.terms_accepted = c.terms_accepted;
        c.user.settings = c.settings;
        c.user.id = c.id;

        if (adminToken) {
          c.admin_token = adminToken;
        }
        if (c.user.settings) {
          store.dispatch(toggleCollapsedNav(c.user.settings.navCollapsed));
          store.dispatch(onNavTypeChange(c.user.settings.navType));
          store.dispatch(onTopNavColorChange(c.user.settings.topNavColor));
          store.dispatch(onHeaderNavColorChange(c.user.settings.headerNavColor));
          store.dispatch(onSwitchTheme(c.user.settings.currentTheme));
          store.dispatch(onDirectionChange(c.user.settings.direction));
          store.dispatch(onLocaleChange(c.user.settings.locale));
        }

        setAuth({
          isSignedIn: true,
          data: c,
        });

        // DEV only: publishes window.spDevSession() so this session can be captured
        // into .env and reused by the Auth0 bypass. No-op in every other environment.
        exposeDevSessionDump(c);

        attemptRef.current = 0;

        setTimeout(
          () => {
            setLoginStatus({
              running: false,
              completed: true,
              success: true,
            });
          },
          isXMAS() ? 2000 : 0,
        );
        localStorage.setItem("SalesPark-AuthBasic", JSON.stringify({ id: c.id, name: c.name, email: c.email, picture: c.picture }));
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Auth0 serverAuth: session setup failed", error);
        }
      }
    } catch (error) {
      const attempt = attemptRef.current;
      if (attempt <= MAX_SESSION_BOOTSTRAP_ATTEMPTS) {
        scheduleTransientRetry(attempt);
      } else {
        setLoginStatus({
          running: false,
          completed: true,
          success: false,
        });
      }
    }
  }, [getAccessTokenSilently, getIdTokenClaims, loginStatus.completed, loginWithRedirect, logout, setAuth, user]);

  return { loginStatus, tryMsg, serverAuth };
};
